<script lang="ts">
  import {
    generateSvgFromText, generateSvgFromImage,
    loadFalConfig, saveFalApiKey, PROMPT_PRESETS, type SvgGenerationResult, type StylePresetId
  } from './falaiClient';
  import { svgToTextureGrid } from './patterns';
  import { app } from './store.svelte';
  import { activeVase, updateActiveVaseTexture } from './gpuVaseMaterial';

  interface Props { onClose: () => void; }
  const { onClose }: Props = $props();

  let imageInput: HTMLInputElement | undefined = $state();
  let mode = $state<'text' | 'image'>('text');
  let prompt = $state('');
  let extraDescription = $state('');
  let stylePreset = $state<StylePresetId | null>(null);
  let imageFile = $state<File | null>(null);
  let imagePreviewUrl = $state<string | null>(null);
  let busy = $state(false);
  let result = $state<SvgGenerationResult | null>(null);
  let error = $state('');
  let showApiKey = $state(false);
  let apiKey = $state(loadFalConfig().apiKey);

  function setImage(f: File | null) {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    imageFile = f;
    imagePreviewUrl = f ? URL.createObjectURL(f) : null;
  }

  function onImageChosen(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (f) setImage(f);
  }

  function onImageDrop(e: DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith('image/')) setImage(f);
  }

  async function generate() {
    error = '';
    busy = true;
    try {
      result = mode === 'text'
        ? await generateSvgFromText(prompt, stylePreset)
        : imageFile
          ? await generateSvgFromImage(imageFile, extraDescription, stylePreset)
          : (() => { throw new Error('Lütfen bir görüntü yükleyin'); })();
    } catch (e) {
      error = (e as Error).message;
    } finally {
      busy = false;
    }
  }

  async function applyToVase() {
    if (!result) return;
    error = '';
    try {
      const grid = await svgToTextureGrid(result.svg, 2, 2, 1);
      app.vaseParams = { ...app.vaseParams, texture: grid };
      if (!updateActiveVaseTexture(grid)) {
        // Fallback: trigger CPU rebuild via store mutation
        app.vaseParams = { ...app.vaseParams };
      }
      onClose();
    } catch (e) {
      error = 'Doku uygulanamadı: ' + (e as Error).message;
    }
  }

  function downloadSvg() {
    if (!result) return;
    const blob = new Blob([result.svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pattern-${Date.now()}.svg`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  function saveKey() {
    saveFalApiKey(apiKey.trim());
    showApiKey = false;
  }

  import { onMount } from 'svelte';
  let modalEl: HTMLDivElement | undefined = $state();
  let prevFocus: HTMLElement | null = null;
  onMount(() => {
    prevFocus = document.activeElement as HTMLElement | null;
    queueMicrotask(() => modalEl?.querySelector<HTMLElement>('button:not([disabled]),input:not([disabled])')?.focus());
    return () => prevFocus?.focus?.();
  });
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !modalEl) return;
    const f = Array.from(modalEl.querySelectorAll<HTMLElement>(
      'button:not([disabled]),[href],input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    )).filter((n) => n.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }
</script>

<div class="overlay" onclick={onClose} onkeydown={onKey} role="presentation">
  <div bind:this={modalEl} class="modal" onclick={(e) => e.stopPropagation()} onkeydown={onKey}
    role="dialog" aria-modal="true" aria-labelledby="svg-gen-title" tabindex="-1">
    <header>
      <h2 id="svg-gen-title"><span class="ms">auto_awesome</span> SVG Üretici</h2>
      <button class="close" onclick={onClose} aria-label="close">×</button>
    </header>

    <div class="mode-tabs">
      <button class:active={mode === 'text'} onclick={() => (mode = 'text')}>
        <span class="ms">edit_note</span> Text → SVG
      </button>
      <button class:active={mode === 'image'} onclick={() => (mode = 'image')}>
        <span class="ms">image</span> Image → SVG
      </button>
    </div>

    <div class="body">
      <div class="left-col">
        {#if mode === 'text'}
          <label class="field">
            <span>Prompt</span>
            <textarea rows="4" bind:value={prompt}
              placeholder="Örn: 8 katlı simetrik mandala, ince hatlarla"></textarea>
          </label>
        {:else}
          <div class="field">
            <span>Görüntü</span>
            <input type="file" accept="image/*" bind:this={imageInput}
              onchange={onImageChosen} style="display:none" />
            <button class="dropzone" type="button"
              ondragover={(e) => e.preventDefault()}
              ondrop={onImageDrop}
              onclick={() => imageInput?.click()}>
              {#if imagePreviewUrl}
                <img src={imagePreviewUrl} class="dz-preview" alt="upload" />
                <span class="dz-name">{imageFile?.name}</span>
                <span class="dz-hint">Değiştirmek için tıkla / bırak</span>
              {:else}
                <span class="ms big">cloud_upload</span>
                <span>Görüntüyü buraya bırak veya tıklayıp seç</span>
                <span class="dz-hint">PNG · JPG · WebP · SVG</span>
              {/if}
            </button>
          </div>
          <label class="field">
            <span>Ek Açıklama (sistem promptlarına ekstra)</span>
            <textarea rows="3" bind:value={extraDescription}
              placeholder="Örn: Sadece dış konturları al, boşlukları doldurma"></textarea>
          </label>
        {/if}

        <div class="field">
          <span>Stil Önayarı</span>
          <div class="chips">
            <button class:active={stylePreset === null} onclick={() => (stylePreset = null)}>—</button>
            {#each PROMPT_PRESETS as p (p.id)}
              <button class:active={stylePreset === p.id} onclick={() => (stylePreset = p.id)}>{p.label}</button>
            {/each}
          </div>
        </div>

        <div class="actions">
          <button class="primary" onclick={generate} disabled={busy || (mode === 'text' ? !prompt.trim() : !imageFile)}>
            {#if busy}
              <span class="ms spin">progress_activity</span> Üretiliyor…
            {:else}
              <span class="ms">auto_awesome</span> Üret
            {/if}
          </button>
          <button class="ghost" onclick={() => (showApiKey = !showApiKey)}>
            <span class="ms">key</span> API
          </button>
        </div>

        {#if showApiKey}
          <div class="api-row">
            <input type="password" bind:value={apiKey} placeholder="Fal.AI API Key" />
            <button class="ghost" onclick={saveKey}>Kaydet</button>
          </div>
          <p class="hint">API anahtarı yoksa deterministik örnek desen üretilir (mock).</p>
        {/if}

        {#if error}<p class="error">{error}</p>{/if}
      </div>

      <div class="right-col">
        <div class="preview-frame">
          {#if result}
            <div class="svg-host">{@html result.svg}</div>
            <div class="badge">{result.source === 'fal' ? 'Fal.AI' : 'mock'}</div>
          {:else}
            <p class="muted">Üretim sonrası önizleme burada görünecek</p>
          {/if}
        </div>
        {#if result}
          <div class="result-actions">
            <button class="ghost" onclick={downloadSvg}>
              <span class="ms">download</span> .svg İndir
            </button>
            <button class="primary" onclick={applyToVase}>
              <span class="ms">brush</span> Vazoya Uygula
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.65);
    display: grid; place-items: center; z-index: 200;
    backdrop-filter: blur(4px);
  }
  .modal {
    background: var(--bg-0);
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    width: 760px; max-width: 95vw; max-height: 90vh;
    display: flex; flex-direction: column;
    box-shadow: 0 20px 80px rgba(0,0,0,0.55);
  }
  header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid var(--border-soft);
    background: linear-gradient(90deg, var(--bg-header-from) 0%, var(--bg-header-to) 100%);
    border-radius: 14px 14px 0 0;
  }
  h2 {
    margin: 0; font-size: 15px; font-weight: 600;
    display: inline-flex; align-items: center; gap: 8px;
    color: var(--text-bright);
  }
  h2 .ms { color: var(--accent); }
  .close {
    background: transparent; border: none; color: var(--text-muted);
    cursor: pointer; font-size: 22px; line-height: 1; padding: 0 4px;
  }
  .close:hover { color: var(--text-bright); }

  .mode-tabs {
    display: flex; gap: 4px; padding: 8px 20px 0;
  }
  .mode-tabs button {
    flex: 1; background: var(--chip-bg); color: var(--text-3);
    border: 1px solid transparent; padding: 8px 12px;
    border-radius: 8px 8px 0 0; cursor: pointer; font-family: inherit;
    font-size: 12px; font-weight: 500;
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  }
  .mode-tabs button.active {
    background: var(--accent-soft); color: #bfe6ff;
    border-color: rgba(26, 122, 201, 0.3); border-bottom-color: transparent;
  }
  .mode-tabs .ms { font-size: 16px; }

  .body {
    display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
    padding: 14px 20px; overflow: auto;
  }

  .field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; }
  .field > span {
    font-size: 11px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  textarea, input[type='text'], input[type='password'], input[type='file'] {
    background: var(--bg-viewer-from); color: var(--text-1);
    border: 1px solid var(--border-soft); border-radius: 6px;
    padding: 8px 10px; font-family: inherit; font-size: 12px;
    resize: vertical; width: 100%; box-sizing: border-box;
  }
  textarea:focus, input:focus { outline: none; border-color: var(--accent); }

  .chips { display: flex; gap: 4px; flex-wrap: wrap; }
  .chips button {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid transparent; border-radius: 999px;
    padding: 4px 10px; font-size: 11px; cursor: pointer; font-family: inherit;
  }
  .chips button.active {
    background: var(--accent); color: white;
  }

  .actions { display: flex; gap: 6px; margin-top: 8px; }
  .actions .primary, .result-actions .primary {
    flex: 1; background: var(--accent); color: white;
    border: none; padding: 9px 14px; border-radius: 8px;
    font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  }
  .actions .primary:disabled { background: var(--chip-bg); color: var(--text-muted); cursor: not-allowed; }
  .actions .primary:hover:not(:disabled), .result-actions .primary:hover { background: var(--accent-hover); }
  .actions .ghost, .result-actions .ghost {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft); padding: 9px 14px;
    border-radius: 8px; font-size: 12px; cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; gap: 4px;
  }
  .actions .ghost:hover, .result-actions .ghost:hover { background: var(--chip-bg-hover); color: var(--text-bright); }

  .api-row { display: flex; gap: 6px; margin-top: 8px; }
  .api-row input { flex: 1; }
  .hint { font-size: 11px; color: var(--text-muted); margin: 4px 0 0; }
  .error {
    background: rgba(208, 74, 58, 0.18); color: #ffd0d0;
    border: 1px solid rgba(208, 74, 58, 0.3);
    border-radius: 6px; padding: 8px 10px; font-size: 12px; margin: 8px 0 0;
  }

  .preview-frame {
    aspect-ratio: 1; background: var(--bg-viewer-from);
    border: 1px dashed var(--border-dashed); border-radius: 8px;
    display: grid; place-items: center; position: relative;
    overflow: hidden;
  }
  .svg-host { width: 90%; height: 90%; display: grid; place-items: center; }
  .svg-host :global(svg) { width: 100%; height: 100%; }
  .badge {
    position: absolute; top: 8px; left: 8px;
    background: rgba(43, 182, 115, 0.2); color: #2bb673;
    font-size: 10px; padding: 2px 8px; border-radius: 4px; font-weight: 600;
  }
  .muted { color: var(--text-muted); font-size: 12px; }
  .upload-preview {
    width: 100%; max-height: 100px; object-fit: cover;
    border-radius: 6px; border: 1px solid var(--border-soft);
    margin-bottom: 12px;
  }

  .dropzone {
    background: var(--bg-viewer-from);
    border: 1.5px dashed var(--border-dashed);
    border-radius: 8px;
    padding: 22px 12px;
    cursor: pointer;
    color: var(--text-muted);
    font-family: inherit;
    font-size: 12px;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    transition: border-color 0.12s, background 0.12s;
  }
  .dropzone:hover { border-color: var(--accent); color: var(--text-2); background: rgba(26, 122, 201, 0.05); }
  .dropzone .ms.big { font-size: 28px; color: var(--accent); }
  .dz-preview {
    max-width: 100%; max-height: 96px; object-fit: contain;
    border-radius: 6px; border: 1px solid var(--border-soft);
  }
  .dz-name { color: var(--text-2); font-weight: 500; word-break: break-all; }
  .dz-hint { font-size: 10.5px; color: var(--text-muted); }
  .result-actions { display: flex; gap: 6px; margin-top: 10px; }
</style>
